import React from 'react'
import { FormProvider } from 'react-hook-form'
import { MemoryRouter, useParams, useRouteMatch } from 'react-router-dom'
import { Button, Dialog, DialogActions, DialogContent } from '@mui/material'
import { v4 as uuidv4 } from 'uuid'
import { getDashId } from '@edge/spark/io-dashboards'
import CardConfigurator from '..'
import { getSearchParam } from '@edge/spark/utils'
import SearchCardConfigurationModalTitle from './SearchCardConfigurationModalTitle'
import CancelConfirmDialog from './CancelConfirmDialog'
import DeleteConfirmDialog from './DeleteConfirmDialog'
import {
    useCloseAccessoryDrawerMutation,
    useAddDashboardCardMutation,
    useUpdateDashboardCardMutation,
    useDeleteDashboardCardMutation,
    type DashboardCardDef,
} from '@edge/spark/io-dashboards'
import { convertDashboardCardFormatFieldsToGenericWidgetConfig } from '../converters'
import { useCardConfigForm } from '../useCardConfigForm'
import { useIsCardEditbleByUser } from './SearchCardConfigurationModal.utils'
import { LoadingButton, useBreakpoint } from '@edge/spark/ui-mui'
import { createDialog } from '@edge/spark/feature-dialog'

export interface WidgetConfigurationModalProps {
    widgetDef: DashboardCardDef
    addingWidget: boolean
    handleSaveConfig: (cardDef: DashboardCardDef) => void
    handleClose: () => void
    handleDeleteCard: (cardId: string) => void
    cardEditClicked?: boolean
}

export const SearchCardConfigurationForm: React.FC<WidgetConfigurationModalProps> = ({
    widgetDef,
    addingWidget,
    handleClose,
}: WidgetConfigurationModalProps) => {
    const { dashId: paramsDashId } = useParams<{ dashId?: string }>()
    const { params: matchParams } = useRouteMatch<{ dashboardId: string }>('/dashboards/:dashId') || {}
    const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false)
    const [confirmCancelOpen, setConfirmCancelOpen] = React.useState(false)

    const [closeDrawer] = useCloseAccessoryDrawerMutation()
    const [addCard, { loading: addCardIsLoading }] = useAddDashboardCardMutation()
    const [updateCard, { loading: updateCardIsLoading }] = useUpdateDashboardCardMutation()
    const [deleteCard] = useDeleteDashboardCardMutation()
    const userIdFromSearchParams = getSearchParam({
        paramName: 'uid',
        searchParams: new URL(`${document.location}`).searchParams,
    })
    const userId = userIdFromSearchParams

    const dashId = paramsDashId || matchParams?.dashboardId || getDashId()

    const handleClickDelete = () => setConfirmDeleteOpen(true)
    const handleNegativeConfirmDelete = () => {
        setConfirmDeleteOpen(false)
    }
    const handleAfirmConfirmDelete = async () => {
        setConfirmDeleteOpen(false)
        handleClose()

        await deleteCard({ variables: { userId, dashId, cardId: widgetDef.id } })
    }

    const handleClickCancel = (): void => setConfirmCancelOpen(true)
    const handleAfirmConfirmCancel = (): void => {
        setConfirmCancelOpen(false)
        handleClose()
    }
    const handleNegativeConfirmCancel = (): void => {
        setConfirmCancelOpen(false)
    }

    const methods = useCardConfigForm(widgetDef)

    const handleSubmit = methods.handleSubmit(
        async (values) => {
            await Promise.resolve()

            const config = convertDashboardCardFormatFieldsToGenericWidgetConfig(
                values.format,
                values.aql,
                values.type,
                values.workflows,
            )

            if (addingWidget) {
                const card =
                    widgetDef?.config?.search || widgetDef?.config?.contactLogSearch
                        ? {
                              ...widgetDef,
                              id: uuidv4(),
                              config,
                          }
                        : {
                              ...widgetDef,
                          }

                await addCard({
                    variables: { userId, dashId, card: JSON.stringify(card) },
                })
            } else {
                const card =
                    widgetDef?.config?.search || widgetDef?.config?.contactLogSearch
                        ? {
                              ...widgetDef,
                              config,
                          }
                        : {
                              ...widgetDef,
                          }

                await updateCard({
                    variables: {
                        card: JSON.stringify(card),
                        cardId: card.id,
                        dashId,
                        userId,
                    },
                })
            }

            await closeDrawer()
            handleClose()
        },
        (fieldErrors) => {
            const messages = Object.entries(fieldErrors)
                .map(([key, err]) => (key ? `[${key}] ${err.message}` : err.message))
                .join(' ')
            createDialog({ message: `There was an error with your card configuration -- ${messages}` })
            console.error({ fieldErrors })
        },
    )

    const isContactCard = !!widgetDef.config?.contactLogSearch
    const modalTitle = `${addingWidget ? 'Add a' : 'Configure'}${isContactCard ? ' Contact Log ' : ' '}Card`
    const allowEditing = useIsCardEditbleByUser(widgetDef)

    const isXsDevice = useBreakpoint() === 'xs'

    return (
        <FormProvider {...methods}>
            <DeleteConfirmDialog
                open={confirmDeleteOpen}
                onSuccess={handleAfirmConfirmDelete}
                onFailure={handleNegativeConfirmDelete}
                type={widgetDef.type}
            />
            <CancelConfirmDialog
                open={confirmCancelOpen}
                onSuccess={handleAfirmConfirmCancel}
                onFailure={handleNegativeConfirmCancel}
                type={widgetDef.type}
            />

            <Dialog
                open
                onClose={handleClickCancel}
                fullWidth
                fullScreen={isXsDevice}
                disablePortal // Not ready for mui5 (see commit)
                maxWidth="md"
            >
                <SearchCardConfigurationModalTitle handleClose={handleClickCancel}>
                    {modalTitle}
                </SearchCardConfigurationModalTitle>
                <DialogContent sx={{ pb: 0 }}>
                    <CardConfigurator widgetDef={widgetDef} readOnly={!allowEditing} />
                </DialogContent>
                <DialogActions>
                    <Button autoFocus onClick={handleClickCancel} color="primary">
                        Cancel
                    </Button>
                    {!addingWidget && (
                        <Button autoFocus onClick={handleClickDelete} color="primary">
                            Delete
                        </Button>
                    )}

                    <LoadingButton
                        autoFocus
                        color="primary"
                        variant="contained"
                        loading={addCardIsLoading || updateCardIsLoading}
                        disabled={addCardIsLoading || updateCardIsLoading}
                        onClick={handleSubmit}
                    >
                        {addingWidget ? 'Create' : 'Update'}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </FormProvider>
    )
}

export const SearchCardConfigurationModal: React.VFC<WidgetConfigurationModalProps> = (props) => {
    return (
        <MemoryRouter>
            <SearchCardConfigurationForm {...props} />
        </MemoryRouter>
    )
}

export default SearchCardConfigurationModal
